import { ReactNode, useLayoutEffect, useState } from 'react';

import { EditorPlugin } from '@draft-js-plugins/editor';
import {
  ContentBlock,
  EditorState,
  Modifier,
  RawDraftContentState,
  SelectionState,
} from 'draft-js';
import styled from 'styled-components';

import { IconDropdown } from '@components/icons/dropdown';
import { IconPluginPlaceholder } from '@components/icons/text-editor-plugin-placeholder';
import { Menu } from '@components/menu';
import { theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';
import { findEntitiesByType } from '@utils/draftjs';

import { useTextEditorContext } from './text-editor';

import { useContainerSize } from '../component-helpers';

export type PlaceholderMap = Record<string, string>;

interface Props {
  placeholderMap: PlaceholderMap;
  label: string;
}
export function TextEditorPluginPlaceholder({ placeholderMap, label }: Props) {
  const { containerRef, editorRef, registerPlugin, setEditorState, editorState, readOnly } =
    useTextEditorContext();

  const [plugin] = useState(() => createPlaceholderPlugin(placeholderMap));

  useLayoutEffect(() => {
    registerPlugin('placeholder', plugin);
  }, [plugin, registerPlugin]);

  const keys = Object.keys(placeholderMap);

  const size = useContainerSize(containerRef);
  const isSmall = size.width < 500;
  const ICON_COLOR = 'purple50';

  if (readOnly) return null;
  return (
    <Menu
      data-c="placeholder-trigger"
      aria-label={label}
      buttonProps={{
        iconLeft: isSmall ? undefined : <IconPluginPlaceholder color={ICON_COLOR} />,
        iconRight: isSmall ? (
          <IconPluginPlaceholder color={ICON_COLOR} />
        ) : (
          <IconDropdown color={ICON_COLOR} />
        ),
        text: isSmall ? '' : label,
        variant: 'secondary',
        font: 'semibold12',
        color: 'textSecondary',
        size: 'sm',
        textStyle: { margin: `0 ${pxToRem(4)}` },
      }}
    >
      {keys.map((placeholder) => (
        <Menu.Item
          label={placeholderMap[placeholder]}
          key={placeholder}
          onSelect={(event) => {
            const newState = insertPlaceholder(
              editorState,
              placeholderMap[placeholder],
              placeholder,
            );
            if (newState) {
              setEditorState(newState);
            }

            setTimeout(() => {
              editorRef.current?.focus();
            }, 200);
          }}
        />
      ))}
    </Menu>
  );
}

export interface PlaceholderEditorPlugin extends EditorPlugin {
  placeholderMap: PlaceholderMap;
}

function createPlaceholderPlugin(placeholderMap: PlaceholderMap): PlaceholderEditorPlugin {
  function Placeholder({ children }: { children: ReactNode }) {
    return <PlaceholderContainer>{children}</PlaceholderContainer>;
  }

  return {
    placeholderMap,
    decorators: [
      {
        strategy: findEntitiesByType('placeholder'),
        component: Placeholder,
      },
    ],
  };
}

const PlaceholderContainer = styled.span({
  display: 'inline-block',
  padding: '0 0.5rem',
  background: theme.color.blue20,
  borderRadius: '0.125rem',
  color: theme.color.elementBlue100,
  lineHeight: '1.375rem',
});

// Insert the placeholder into the draftjs instance. This means adding
// the 'placeholder' entity into the entity map and also add the
// placeholder component into the content.
function insertPlaceholder(editorState: EditorState, label: string, data: string) {
  const currentContent = editorState.getCurrentContent();
  const selection = editorState.getSelection();
  const entity = currentContent.createEntity('placeholder', 'IMMUTABLE', data);
  const entityKey = entity.getLastCreatedEntityKey();
  const textWithEntity = Modifier.insertText(
    currentContent,
    selection,
    label,
    undefined,
    entityKey,
  );
  return EditorState.push(editorState, textWithEntity, 'apply-entity');
}

// Convert {{ placeholder }} tags in the text-editor to proper placeholder
// components with proper entity data.
export function convertPlaceholders(
  editorState: EditorState,
  placeholderMap: Record<string, string>,
) {
  const selectionsToReplace: Array<{ selectionState: SelectionState; match: string }> = [];
  // Match any {{ stuff }} key
  const regex = new RegExp('\\{\\{\\s\\w+\\s\\}\\}|\\{\\{\\w+\\}\\}', 'g');

  let contentState = editorState.getCurrentContent();
  contentState.getBlockMap().forEach((contentBlock) => {
    if (!contentBlock) return;

    findWithRegex(
      regex,
      (start: number, end: number, matchStr: string) => {
        if (placeholderMap.hasOwnProperty(matchStr)) {
          // This is one of the placeholder keys, not just some {{ random }} key
          const blockKey = contentBlock.getKey();
          const blockSelection = SelectionState.createEmpty(blockKey).merge({
            anchorOffset: start,
            focusOffset: end,
          });
          selectionsToReplace.push({
            selectionState: blockSelection,
            match: matchStr,
          });
        }
      },
      contentBlock,
    );
  });

  if (selectionsToReplace.length === 0) {
    return editorState;
  }

  // Need to reverse the order of selections to replace.
  // Otherwise the stored offsets to replace (in the array above) will
  // mess up on each insert since the placeholder may not have the same
  // number of characters as the string being replaced.
  selectionsToReplace.reverse().forEach(({ selectionState, match }) => {
    const entity = contentState.createEntity('placeholder', 'IMMUTABLE', match);
    const entityKey = entity.getLastCreatedEntityKey();
    contentState = Modifier.replaceText(
      contentState,
      selectionState,
      placeholderMap[match],
      undefined,
      entityKey,
    );
  });

  let es = EditorState.push(editorState, contentState, 'change-block-data');
  return EditorState.forceSelection(es, contentState.getSelectionAfter());
}

function findWithRegex(regex: RegExp, callback: any, contentBlock?: ContentBlock) {
  if (!contentBlock) return;

  const text = contentBlock.getText();
  let matchArr, start, end;
  while ((matchArr = regex.exec(text)) !== null) {
    start = matchArr.index;
    end = start + matchArr[0].length;
    const matchStr = matchArr[0];
    callback(start, end, matchStr);
  }
}

// Convert placeholder entities to bracket tags in the editorState
// So the API can just search and replace the {{ tags }} without
// having to know the complicated DraftJS object.
export function placeholdersToBrackets(raw: RawDraftContentState): RawDraftContentState {
  let blx = [...raw.blocks].map((block) => {
    let obj = { ...block };
    block.entityRanges.forEach((er) => {
      const isPlaceholder = raw.entityMap[er.key].type === 'placeholder';
      if (!isPlaceholder) return;

      let str = block.text.substring(er.offset, er.offset + er.length);
      obj.text = obj.text.replace(str, raw.entityMap[er.key].data.toString());
    });
    return obj;
  });
  return {
    ...raw,
    blocks: blx,
  };
}
